<script setup>
import { ref, onMounted } from 'vue';
import { useAuthStore } from '@/stores';
import shaka from 'shaka-player/dist/shaka-player.ui.js';
import { v4 } from 'uuid';
import { Logger } from '../../../middlewares/sentry';
import VideoService from '../../../services/VideoService';

const authStore = useAuthStore();

const videoId = v4();

const props = defineProps({
  videoName: {
    type: String,
    required: true,
  },
});

const shakaPlayer = ref(null);
const videoPlayer = ref(null);
const videoContainer = ref(null);

function onError(error) {
  Logger.error('Error code' + error.code + 'object' + error);
}

function onErrorEvent(event) {
  onError(event.detail);
}

function initializeShakaPlayer() {
  shaka.polyfill.installAll();
  if (shaka.Player.isBrowserSupported()) {
    shakaPlayer.value = new shaka.Player();

    shakaPlayer.value
      .getNetworkingEngine()
      .registerRequestFilter((type, request) => {
        if (
          type == shaka.net.NetworkingEngine.RequestType.MANIFEST ||
          type == shaka.net.NetworkingEngine.RequestType.SEGMENT
        ) {
          request.headers['x-access-token'] = authStore.userToken;
        }
      });

    shakaPlayer.value.addEventListener('error', onErrorEvent);

    shakaPlayer.value
      .attach(videoPlayer.value)
      .then(() => {
        return shakaPlayer.value.load(
          VideoService.getManifestUrlByName(props.videoName),
        );
      })
      .catch(() => {
        shakaPlayer.value.load(
          VideoService.getTokenizedManifestUrlByName(
            props.videoName,
            authStore.userToken,
          ),
        );
      })
      .catch(onError);

    window.player = shakaPlayer.value;
  } else {
    Logger.error('Browser not supported');
  }
}

function initializeShakaUI() {
  const ui = new shaka.ui.Overlay(
    shakaPlayer.value,
    videoContainer.value,
    videoPlayer.value,
  );

  window.ui = ui;
}

onMounted(() => {
  initializeShakaPlayer();
  initializeShakaUI();
});
</script>

<template>
  <div class="video">
    <div
      ref="videoContainer"
      data-shaka-player-container
      class="video-container"
    >
      <video
        :id="videoId"
        ref="videoPlayer"
        data-shaka-player
        width="100%"
        alt=""
        preload="metadata"
      ></video>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video {
  margin: 0 auto;
  max-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.video-container {
  height: 100%;
  width: 90vw;
  max-height: 90vh;
}
</style>
